import React, { useEffect, useState } from "react";
import { Download, Smartphone, X } from "lucide-react";

/**
 * Sidebar PWA install widget.
 *
 * Behavior:
 *   • Chrome / Edge / Android: captures `beforeinstallprompt`, hides itself
 *     while not available, shows an "Install app" button that triggers the
 *     native prompt.
 *   • Safari iOS / iPadOS: detects iOS and shows a tiny instructional card
 *     (Share → Add to Home Screen) since iOS has no programmatic prompt.
 *   • Already-installed (running in standalone display mode): hides itself.
 *   • Once dismissed, stays hidden for the session.
 */
export default function InstallPWA() {
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    // Hide if already installed (standalone)
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) {
      setDismissed(true);
      return;
    }

    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => setDismissed(true);

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);

    // iOS doesn't fire beforeinstallprompt but is installable via Safari Share.
    const ua = window.navigator.userAgent || "";
    const isIos = /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
    const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS/i.test(ua);
    if (isIos && isSafari) setShowIosHint(true);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (dismissed) return null;
  if (!deferred && !showIosHint) return null;

  const triggerInstall = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      await deferred.userChoice;
    } catch (_e) {
      // user cancelled or browser rejected — silently ignore
    } finally {
      setDeferred(null);
    }
  };

  return (
    <div
      className="mx-2 sm:mx-3 mb-2 p-2.5 sm:p-3 rounded-md border border-zinc-800 bg-zinc-950 text-white relative"
      data-testid="install-pwa"
    >
      <button
        onClick={() => setDismissed(true)}
        className="absolute top-1.5 right-1.5 text-zinc-500 hover:text-white"
        title="Dismiss"
        data-testid="install-pwa-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
      <div className="flex items-center gap-2 mb-1">
        <Smartphone className="w-4 h-4" />
        <span className="text-[10px] uppercase tracking-widest font-bold">Install on phone</span>
      </div>
      {deferred ? (
        <>
          <p className="text-xs text-zinc-400 leading-snug mb-2">
            Get the full-screen Happy Retreats app on this device.
          </p>
          <button
            onClick={triggerInstall}
            className="w-full inline-flex items-center justify-center gap-2 text-xs font-semibold bg-white text-zinc-950 rounded px-3 py-2 hover:bg-zinc-200 transition-colors"
            data-testid="install-pwa-button"
          >
            <Download className="w-3.5 h-3.5" />
            Install app
          </button>
        </>
      ) : (
        <p className="text-xs text-zinc-400 leading-snug">
          Tap <span className="font-semibold text-white">Share</span> →{" "}
          <span className="font-semibold text-white">Add to Home Screen</span> in Safari to install.
        </p>
      )}
    </div>
  );
}
